import { ReactElement, useMemo, useCallback } from 'react';
import { CardActionArea } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTag as TagIcon,
  faCalendar as CalendarIcon,
} from '@fortawesome/free-solid-svg-icons';

import {
  VehicleInfo,
  RouteStateChip,
  StopsProgressInfo,
  DistanceInfo,
  DescriptionWithIconBox,
  UserInfo,
} from '~components/index';
import { getRoutesStopsInfoByApiTransaction } from '~utils/route';
import { useUtcOffset } from '~hooks/index';

import { AddOrdersToRouteMenuRouteCardProps } from './types';
import {
  AddOrdersToRouteMenuCardContainer,
  AddOrdersToRouteMenuCardContent,
  AddOrdersToRouteMenuCardVehicleInfoBox,
  AddOrdersToRouteMenuCardVehicleInfoBoxTitleContainer,
  AddOrdersToRouteMenuCardRouteInfoBox,
  AddOrdersToRouteMenuCardRouteInfoBoxRow,
} from './styles';

const AddOrdersToRouteMenuRouteCard = ({
  route,
  onClick,
}: AddOrdersToRouteMenuRouteCardProps): ReactElement => {
  const formatterDateUtcOffset = useUtcOffset();

  const fullNameDriver = useMemo((): string => {
    const driver = route.driver;

    if (!driver) {
      return 'Sin Asignar';
    }

    return `${driver.lastName}, ${driver.firstName}`;
  }, [route.driver]);

  const stopsProgressData = useMemo(
    () => getRoutesStopsInfoByApiTransaction(route.routeItems),
    [route.routeItems],
  );

  const handleClick = useCallback(() => {
    onClick(route);
  }, [onClick, route]);

  return (
    <AddOrdersToRouteMenuCardContainer variant="outlined">
      <CardActionArea onClick={handleClick} disableRipple disableTouchRipple>
        <AddOrdersToRouteMenuCardContent>
          <AddOrdersToRouteMenuCardVehicleInfoBox>
            <AddOrdersToRouteMenuCardVehicleInfoBoxTitleContainer>
              <VehicleInfo
                vehicle={route.vehicle.name}
                fontSize={14}
                color={route.vehicle.color}
              />
            </AddOrdersToRouteMenuCardVehicleInfoBoxTitleContainer>

            <RouteStateChip stateId={route.routeStateTypeId} size="small" />
          </AddOrdersToRouteMenuCardVehicleInfoBox>

          <AddOrdersToRouteMenuCardRouteInfoBox>
            <AddOrdersToRouteMenuCardRouteInfoBoxRow>
              <DescriptionWithIconBox
                icon={<FontAwesomeIcon icon={TagIcon} />}
                description={`#${route.routeCode.toUpperCase()}`}
                size={11}
                fontWeight={500}
              />

              <DistanceInfo distance={route.totalKm} fontSize={11} />

              <DescriptionWithIconBox
                icon={<FontAwesomeIcon icon={CalendarIcon} />}
                description={formatterDateUtcOffset(
                  route.scheduledDateTime,
                  'DD/MM/YYYY HH:mm',
                )}
                size={11}
              />
            </AddOrdersToRouteMenuCardRouteInfoBoxRow>

            <AddOrdersToRouteMenuCardRouteInfoBoxRow justifyContent="space-between">
              <UserInfo
                name={fullNameDriver}
                urlAvatar={route.driver?.avatarFile?.url}
                fontSize={11}
              />

              <StopsProgressInfo
                {...stopsProgressData}
                enabledIcon
                fontSize={11}
              />
            </AddOrdersToRouteMenuCardRouteInfoBoxRow>
          </AddOrdersToRouteMenuCardRouteInfoBox>
        </AddOrdersToRouteMenuCardContent>
      </CardActionArea>
    </AddOrdersToRouteMenuCardContainer>
  );
};

export default AddOrdersToRouteMenuRouteCard;
